import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Lenis from '@studio-freight/lenis';
import {
  Shield,
  Zap,
  ChevronRight,
  Brain,
  Network,
  Binary,
  LineChart,
  Search,
  Terminal,
  Eye,
  Database,
  Code,
  Activity,
  Workflow,
  Microscope,
  Share2,
  Boxes
} from 'lucide-react';
import BlockchainScene from './components/BlockchainScene';
import { GridSection, GridBlock } from './components/GridBlock';
import ScrollTracker from './components/ScrollTracker';
import { HorizontalAccordion } from './components/HorizontalAccordion'; // Adjust the path as needed
import NetworkVisualization from './components/NetworkVisualization';
import ChainOfThoughtAnimation from './components/ChainOfThoughtAnimation';
import { Canvas } from '@react-three/fiber'
import Logo from './assets/favicooon.png';


const App = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    lenis.on('scroll', handleScroll);

    return () => {
      lenis.destroy();
    };
  }, []);

  return (
    <div className="relative min-h-screen bg-[#0A0A0B] text-white overflow-hidden">
      <div className="fixed inset-0">
        <BlockchainScene />
      </div>

      <ScrollTracker />

      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isScrolled ? 'bg-[#0A0A0B]/90 backdrop-blur-md py-4' : 'py-6'
        }`}
      >
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src={Logo} alt="WALL-E Logo" className="w-8 h-8" />
              <span className="text-xl font-bold tracking-tight">WALL-E</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a
                href="#features"
                className="text-sm font-medium text-gray-400 hover:text-white transition-colors"
              >
                Features
              </a>
              <a
                href="#how-it-works"
                className="text-sm font-medium text-gray-400 hover:text-white transition-colors"
              >
                How It Works
              </a>
              <a
                href="#technology"
                className="text-sm font-medium text-gray-400 hover:text-white transition-colors"
              >
                Technology
              </a>
              <a
                href="#use-cases"
                className="text-sm font-medium text-gray-400 hover:text-white transition-colors"
              >
                Use Cases
              </a>
              <motion.a
                href="/app"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                className="px-4 py-2 bg-[#02577A] text-white text-sm font-medium hover:bg-[#02A9F7] transition-colors"
              >
                Launch App
              </motion.a>
            </div>
          </div>
        </div>
      </nav>

      <div className="relative pt-[80px]">
        <div className="grid grid-cols-1 lg:[grid-template-columns:calc((100vw-1600px)/2)_1600px_calc((100vw-1600px)/2)] relative z-10">
          <div className="hidden lg:block"></div>
          <div>
            <div className="absolute inset-0 bg-black opacity-30 z-0"></div>

            <section className="relative min-h-[125vh] pb-32 pt-32 z-10">
              <div className="max-w-[1200px] mx-auto px-6">
                <div className="grid lg:grid-cols-2 gap-16 items-start">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="max-w-2xl sticky top-32"
                  >
                    <div className="inline-flex items-center space-x-2 bg-[#1a1a1a] px-4 py-2 mb-8 mt-12">
                      <Terminal className="w-4 h-4 text-[#02A9F7]" />
                      <span className="text-sm font-mono text-gray-400">
                        AI Powered Analytics Engine
                      </span>
                    </div>
                    <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-8">
                      Understand
                      <br />
                  <span className="bg-[linear-gradient(to_right,#01303f,#02577a,#02a9f7,#89d6fb,#d4f0fc)] bg-200% bg-clip-text text-transparent animate-gradient-shift">
                    On-Chain Data
                  </span>
                      <br />
                      Like Never Before
                    </h1>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <motion.a
                        href="/app"
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.02 }}
                        className="inline-flex items-center justify-center px-8 py-4 bg-[#02577A] text-white font-medium hover:bg-[#02A9F7] transition-colors group"
                      >
                        <span>Start Analyzing</span>
                        <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                      </motion.a>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-white font-medium border border-[#1a1a1a] hover:border-[#02A9F7] transition-colors group"
                      >
                        <span>View Documentation</span>
                        <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                      </motion.button>
                    </div>
                  </motion.div>
                  <div className="lg:h-[600px]" />
                </div>
              </div>
            </section>

            <section className="-mt-20 relative z-10">
              <div className="w-full mx-auto">
                <GridSection cols={4}>
                  <GridBlock
                    colSpan={3}
                    className="flex flex-col justify-center p-16 border border-[#1a1a1a]"
                  >
                    <h2 className="text-4xl font-bold mb-6">What is WALL-E?</h2>
                    <p className="text-gray-400 text-lg leading-relaxed">
                      WALL-E is your gateway to understanding blockchain data. We process and
                      analyze on-chain transactions to provide meaningful insights about wallets,
                      tokens, and market movements.
                    </p>
                  </GridBlock>
                  <GridBlock className="border-0 opacity-0 !bg-transparent" />
                  <GridBlock className="border border-[#1a1a1a] p-12">
                    <motion.div className="bg-[#02577A]/10 w-16 h-16 flex items-center justify-center mb-6">
                      <Search className="w-8 h-8 text-[#02A9F7]" />
                    </motion.div>
                    <h3 className="text-xl font-bold mb-4">Wallet Analysis</h3>
                    <p className="text-gray-400">
                      Track any wallet's transactions, holdings, and interactions across multiple chains.
                    </p>
                  </GridBlock>
                  <GridBlock className="border border-[#1a1a1a] p-12">
                    <motion.div className="bg-[#02577A]/10 w-16 h-16 flex items-center justify-center mb-6">
                      <Activity className="w-8 h-8 text-[#02A9F7]" />
                    </motion.div>
                    <h3 className="text-xl font-bold mb-4">Token Tracking</h3>
                    <p className="text-gray-400">
                      Monitor token transfers, liquidity changes, and price movements in real-time.
                    </p>
                  </GridBlock>
                  <GridBlock className="border border-[#1a1a1a] p-12">
                    <motion.div className="bg-[#02577A]/10 w-16 h-16 flex items-center justify-center mb-6">
                      <Code className="w-8 h-8 text-[#02A9F7]" />
                    </motion.div>
                    <h3 className="text-xl font-bold mb-4">Developer API</h3>
                    <p className="text-gray-400">
                      Access blockchain data programmatically through our comprehensive API.
                    </p>
                  </GridBlock>
                  <GridBlock className="border-0 opacity-0 !bg-transparent" />
                </GridSection>
              </div>
            </section>

            <div className="bg-[#0D0D0E]">
              <div className="w-full">
                <section id="how-it-works">
                  <GridSection cols={3}>
                    <GridBlock colSpan={3} className="flex flex-col justify-center p-16 border border-[#1a1a1a]">
                      <h2 className="text-4xl font-bold mb-6">How It Works</h2>
                      <p className="text-gray-400 text-lg max-w-2xl">
                        Our platform combines cutting-edge blockchain indexing with advanced analytics to provide real-time insights and analysis.
                      </p>
                    </GridBlock>
          
                    <GridBlock colSpan={3} className="p-0 border-0">
                      <HorizontalAccordion
                        items={[
                          {
                            icon: <Database className="w-8 h-8 text-[#02A9F7]" />,
                            title: 'Data Collection',
                            description:
                              'We continuously index blockchain data across multiple networks, ensuring you have access to the latest transactions and state changes.',
                          },
                          {
                            icon: <Brain className="w-8 h-8 text-[#02A9F7]" />,
                            title: 'Analysis & Processing',
                            description:
                              'Our advanced algorithms process raw blockchain data to extract meaningful patterns and insights about wallets and tokens.',
                          },
                          {
                            icon: <LineChart className="w-8 h-8 text-[#02A9F7]" />,
                            title: 'Visualization & Insights',
                            description:
                              'Access processed data through our intuitive interface or API, with clear visualizations and actionable insights.',
                          },
                        ]}
                      />
                    </GridBlock>
                  </GridSection>
              </section>

                <section id="technology">
                  <div>
                    <GridSection cols={2}>
                      <GridBlock className="border border-[#1a1a1a] p-16">
                        <h2 className="text-4xl font-bold mb-8">
                          Powered by Advanced
                          <br />
                          <span className="bg-[linear-gradient(to_right,#01303f,#02577a,#02a9f7,#89d6fb,#d4f0fc)] bg-200% bg-clip-text text-transparent animate-gradient-shift">
                            Technology
                          </span>
                        </h2>
                        <p className="text-gray-400 text-lg mb-12">
                          Our platform combines cutting-edge blockchain indexing with advanced analytics to provide real-time insights and analysis.
                        </p>
                        <div className="space-y-12">
                          <div className="flex items-start space-x-6">
                            <div className="bg-[#02577A]/10 p-4">
                              <Binary className="w-6 h-6 text-[#02A9F7]" />
                            </div>
                            <div>
                              <h3 className="text-xl font-bold mb-2">High-Speed Indexing</h3>
                              <p className="text-gray-400">
                                Process millions of transactions per second with our optimized indexing engine.
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start space-x-6">
                            <div className="bg-[#02577A]/10 p-4">
                              <Brain className="w-6 h-6 text-[#02A9F7]" />
                            </div>
                            <div>
                              <h3 className="text-xl font-bold mb-2">AI Analysis</h3>
                              <p className="text-gray-400">
                                Advanced machine learning models for pattern detection and prediction.
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start space-x-6">
                            <div className="bg-[#02577A]/10 p-4">
                              <Network className="w-6 h-6 text-[#02A9F7]" />
                            </div>
                            <div>
                              <h3 className="text-xl font-bold mb-2">Multi-Chain Support</h3>
                              <p className="text-gray-400">
                                Analyze data across multiple blockchain networks seamlessly.
                              </p>
                            </div>
                          </div>
                        </div>
                      </GridBlock>
                      <GridBlock className="border border-[#1a1a1a] p-16">
                        <div className="bg-[#1a1a1a] p-8 h-full rounded-lg">
                          <pre className="text-sm font-mono text-gray-400">
{`// WALL-E API Example
const analysis = await WALLE.analyze({
  address: "0x742d35Cc6634C0532925a3b844Bc454e4438f44e",
  depth: 2,
  options: {
    patterns: true,
    flows: true,
    predictions: true
  }
});

// Results
{
  patterns: {
    type: "TRADER",
    confidence: 0.92,
    activity: "HIGH"
  },
  flows: {
    incoming: [...],
    outgoing: [...],
    volume: "1,250,000 USD"
  },
  predictions: {
    nextMove: "ACCUMULATION",
    confidence: 0.85
  }
}`}
                          </pre>
                        </div>
                      </GridBlock>
                    </GridSection>
                  </div>
                </section>
                <section id="chain-of-thought">
                  <div>
                    <GridSection cols={1} animate={false}>
                      <GridBlock
                        colSpan={1}
                        className="flex flex-col justify-center p-16 border border-[#1a1a1a]"
                        animate={false}
                      >
                        <h2 className="text-4xl font-bold mb-6">Chain of Thought</h2>
                        <p className="text-gray-400 text-lg max-w-2xl">
                          WALL-E leverages advanced reasoning capabilities to break down complex blockchain data analysis into clear, logical steps.
                        </p>
                      </GridBlock>
                    </GridSection>

                    <GridSection cols={2} animate={false}>
                      <GridBlock className="border border-[#1a1a1a] p-16" animate={false}>
                        <div className="space-y-10">
                          <div className="space-y-6">
                            <h3 className="text-2xl font-bold">Multi-step Reasoning</h3>
                            <p className="text-gray-400 text-lg">
                              WALL-E doesn't just provide answers - it walks through each step of the analysis process, connecting disparate pieces of information into coherent insights.
                            </p>
                          </div>

                          <div className="space-y-8">
                            <div className="flex items-start space-x-6">
                              <div className="bg-[#02577A]/10 p-4 mt-1">
                                <Workflow className="w-6 h-6 text-[#02A9F7]" />
                              </div>
                              <div>
                                <h4 className="text-xl font-bold mb-2">Step-by-step Analysis</h4>
                                <p className="text-gray-400">
                                  Instead of black-box results, see how WALL-E identifies patterns across transaction histories, wallet interactions, and token movements.
                                </p>
                              </div>
                            </div>

                            <div className="flex items-start space-x-6">
                              <div className="bg-[#02577A]/10 p-4 mt-1">
                                <Share2 className="w-6 h-6 text-[#02A9F7]" />
                              </div>
                              <div>
                                <h4 className="text-xl font-bold mb-2">Information Synthesis</h4>
                                <p className="text-gray-400">
                                  WALL-E connects and combines information from multiple chains and data sources, bringing relevant context to your analysis.
                                </p>
                              </div>
                            </div>

                            <div className="flex items-start space-x-6">
                              <div className="bg-[#02577A]/10 p-4 mt-1">
                                <Brain className="w-6 h-6 text-[#02A9F7]" />
                              </div>
                              <div>
                                <h4 className="text-xl font-bold mb-2">Insight Extraction</h4>
                                <p className="text-gray-400">
                                  After gathering and processing data, WALL-E extracts the most valuable insights, highlighting what's most relevant to your query.
                                </p>
                              </div>
                            </div>

                            <div className="flex items-start space-x-6">
                              <div className="bg-[#02577A]/10 p-4 mt-1">
                                <Zap className="w-6 h-6 text-[#02A9F7]" />
                              </div>
                              <div>
                                <h4 className="text-xl font-bold mb-2">Dynamic Adaptation</h4>
                                <p className="text-gray-400">
                                  WALL-E's reasoning process evolves based on discoveries in the data, intelligently adapting its approach to uncover hidden patterns.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </GridBlock>

                      <GridBlock className="border border-[#1a1a1a] p-0 overflow-hidden relative min-h-[700px]" animate={false}>
                        <div className="absolute inset-0">
                          <Canvas orthographic
                            camera={{ position: [10, 10, 10], zoom: 75 }}
                            dpr={[1, 2]}
                            frameloop="always"
                          >
                            <ambientLight intensity={0.4} />
                            <pointLight position={[10, 10, 10]} intensity={1} />
                            <pointLight position={[-10, -10, -10]} intensity={0.5} />
                            <ChainOfThoughtAnimation />
                          </Canvas>
                        </div>
                      </GridBlock>
                    </GridSection>
                  </div>
                </section>

                <section id="use-cases">
                  <div>
                    <GridSection cols={4}>
                      <GridBlock
                        colSpan={4}
                        className="flex flex-col justify-center p-16 border border-[#1a1a1a]"
                      >
                        <h2 className="text-4xl font-bold mb-6">Use Cases</h2>
                        <p className="text-gray-400 text-lg max-w-2xl">
                          Discover how different users leverage WALL-E to understand blockchain data and make informed decisions.
                        </p>
                      </GridBlock>
                      <GridBlock
                        colSpan={4}
                        className="flex flex-col justify-center p-16 border border-[#1a1a1a] relative min-h-[400px]"
                      >
                        <div className="absolute inset-0">
                          <Canvas orthographic camera={{ position: [10, 10, -10], zoom: 125 }}>
                            <ambientLight intensity={0.5} />
                            <pointLight position={[10, 10, 10]} intensity={1} />
                            <NetworkVisualization />
                          </Canvas>
                        </div>
                      </GridBlock>
                      <GridBlock className="border border-[#1a1a1a] p-12">
                        <div className="flex items-start space-x-6">
                          <div className="bg-[#02577A]/10 p-4">
                            <Code className="w-6 h-6 text-[#02A9F7]" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold mb-4">Developers</h3>
                            <p className="text-gray-400 mb-6">
                              Build better dApps with comprehensive blockchain data access.
                            </p>
                            <ul className="space-y-3">
                              <li className="flex items-center text-gray-400">
                                <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                                Track smart contract interactions
                              </li>
                              <li className="flex items-center text-gray-400">
                                <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                                Monitor contract events
                              </li>
                              <li className="flex items-center text-gray-400">
                                <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                                Analyze user behavior
                              </li>
                            </ul>
                          </div>
                        </div>
                      </GridBlock>
                      <GridBlock className="border border-[#1a1a1a] p-12">
                        <div className="flex items-start space-x-6">
                          <div className="bg-[#02577A]/10 p-4">
                            <LineChart className="w-6 h-6 text-[#02A9F7]" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold mb-4">Traders</h3>
                            <p className="text-gray-400 mb-6">
                              Make data-driven trading decisions with real-time insights.
                            </p>
                            <ul className="space-y-3">
                              <li className="flex items-center text-gray-400">
                                <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                                Track whale movements
                              </li>
                              <li className="flex items-center text-gray-400">
                                <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                                Monitor token flows
                              </li>
                              <li className="flex items-center text-gray-400">
                                <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                                Analyze trading patterns
                              </li>
                            </ul>
                          </div>
                        </div>
                      </GridBlock>
                      <GridBlock className="border border-[#1a1a1a] p-12">
                        <div className="flex items-start space-x-6">
                          <div className="bg-[#02577A]/10 p-4">
                            <Microscope className="w-6 h-6 text-[#02A9F7]" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold mb-4">Researchers</h3>
                            <p className="text-gray-400 mb-6">
                              Understand blockchain dynamics and market behavior.
                            </p>
                            <ul className="space-y-3">
                              <li className="flex items-center text-gray-400">
                                <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                                Study network effects
                              </li>
                              <li className="flex items-center text-gray-400">
                                <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                                Analyze token economics
                              </li>
                              <li className="flex items-center text-gray-400">
                                <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                                Research user behavior
                              </li>
                            </ul>
                          </div>
                        </div>
                      </GridBlock>
                      <GridBlock className="border border-[#1a1a1a] p-12">
                        <div className="flex items-start space-x-6">
                          <div className="bg-[#02577A]/10 p-4">
                            <Shield className="w-6 h-6 text-[#02A9F7]" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold mb-4">Security Teams</h3>
                            <p className="text-gray-400 mb-6">
                              Monitor and analyze security-related blockchain activities.
                            </p>
                            <ul className="space-y-3">
                              <li className="flex items-center text-gray-400">
                                <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                                Track suspicious transactions
                              </li>
                              <li className="flex items-center text-gray-400">
                                <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                                Monitor smart contract risks
                              </li>
                              <li className="flex items-center text-gray-400">
                                <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                                Analyze attack patterns
                              </li>
                            </ul>
                          </div>
                        </div>
                      </GridBlock>
                    </GridSection>
                  </div>
                </section>

                <section id="features">
                  <div>
                    <GridSection cols={3}>
                      <GridBlock
                        colSpan={3}
                        className="flex flex-col justify-center p-16 border border-[#1a1a1a]"
                      >
                        <h2 className="text-4xl font-bold mb-6">Platform Features</h2>
                        <p className="text-gray-400 text-lg max-w-2xl">
                          Explore the powerful features that make WALL-E the most comprehensive blockchain analytics platform.
                        </p>
                      </GridBlock>
                      {[
                        {
                          icon: <Search className="w-6 h-6 text-[#02A9F7]" />,
                          title: "Wallet Explorer",
                          description: "Deep dive into any wallet's history, holdings, and interactions."
                        },
                        {
                          icon: <Activity className="w-6 h-6 text-[#02A9F7]" />,
                          title: "Transaction Analysis",
                          description: "Track and analyze individual transactions with detailed insights."
                        },
                        {
                          icon: <Boxes className="w-6 h-6 text-[#02A9F7]" />,
                          title: "Token Analytics",
                          description: "Comprehensive token metrics and movement analysis."
                        },
                        {
                          icon: <Network className="w-6 h-6 text-[#02A9F7]" />,
                          title: "Network Analysis",
                          description: "Visualize and understand network-wide patterns and trends."
                        },
                        {
                          icon: <Brain className="w-6 h-6 text-[#02A9F7]" />,
                          title: "AI Insights",
                          description: "Get smart predictions and pattern recognition powered by AI."
                        },
                        {
                          icon: <Eye className="w-6 h-6 text-[#02A9F7]" />,
                          title: "Real-time Monitoring",
                          description: "Stay updated with live transaction and wallet monitoring."
                        }
                      ].map((feature, index) => (
                        <GridBlock key={index} className="border border-[#1a1a1a] p-12 group">
                          <div className="relative z-10">
                            <div className="bg-[#1a1a1a] w-12 h-12 flex items-center justify-center mb-6">
                              {feature.icon}
                            </div>
                            <h3 className="text-xl font-bold mb-4">{feature.title}</h3>
                            <p className="text-gray-400">{feature.description}</p>
                          </div>
                        </GridBlock>
                      ))}
                    </GridSection>
                  </div>
                </section>

                <section id="pricing">
                  <div>
                    <GridSection cols={2}>
                      <GridBlock
                        colSpan={2}
                        className="flex flex-col justify-center p-16 border border-[#1a1a1a]"
                      >
                        <h2 className="text-4xl font-bold mb-6">Simple Pricing</h2>
                        <p className="text-gray-400 text-lg max-w-2xl">
                          Start analyzing blockchain data for free. Pro features coming soon.
                        </p>
                      </GridBlock>
                      <GridBlock className="border border-[#1a1a1a] p-12">
                        <div className="text-center mb-8">
                          <h3 className="text-xl font-bold mb-2">Free</h3>
                          <div className="text-3xl font-bold text-[#02A9F7] mb-2">Free</div>
                          <p className="text-gray-400">
                            Perfect for getting started with blockchain analysis
                          </p>
                        </div>
                        <ul className="space-y-4 mb-8">
                          <li className="flex items-center text-gray-400">
                            <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                            Wallet analysis
                          </li>
                          <li className="flex items-center text-gray-400">
                            <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                            Transaction tracking
                          </li>
                          <li className="flex items-center text-gray-400">
                            <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                            Basic token analytics
                          </li>
                          <li className="flex items-center text-gray-400">
                            <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                            Real-time monitoring
                          </li>
                          <li className="flex items-center text-gray-400">
                            <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                            Community support
                          </li>
                        </ul>
                        <motion.a
                          href="/app"
                          target="_blank"
                          rel="noopener noreferrer"
                          whileHover={{ scale: 1.02 }}
                          className="w-full py-3 px-4 block text-center bg-[#02A9F7] hover:bg-[#02577A] transition-colors"
                        >
                          Get Started
                        </motion.a>
                      </GridBlock>
                      <GridBlock className="border-2 border-[#02A9F7] p-12">
                        <div className="text-center mb-8">
                          <h3 className="text-xl font-bold mb-2">Pro</h3>
                          <div className="text-3xl font-bold text-[#02A9F7] mb-2">
                            Coming Soon
                          </div>
                          <p className="text-gray-400">
                            Advanced features for power users
                          </p>
                        </div>
                        <ul className="space-y-4 mb-8">
                          <li className="flex items-center text-gray-400">
                            <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                            Everything in Free
                          </li>
                          <li className="flex items-center text-gray-400">
                            <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                            API access
                          </li>
                          <li className="flex items-center text-gray-400">
                            <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                            Advanced AI insights
                          </li>
                          <li className="flex items-center text-gray-400">
                            <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                            Custom alerts
                          </li>
                          <li className="flex items-center text-gray-400">
                            <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                            Priority support
                          </li>
                          <li className="flex items-center text-gray-400">
                            <ChevronRight className="w-4 h-4 text-[#02A9F7] mr-2" />
                            Extended historical data
                          </li>
                        </ul>
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          className="w-full py-3 px-4 bg-[#1a1a1a] border border-[#02A9F7] hover:bg-[#02A9F7] transition-colors"
                        >
                          Coming Soon
                        </motion.button>
                      </GridBlock>
                    </GridSection>
                  </div>
                </section>

                <footer>
                  <div>
                    <GridSection cols={4}>
                      <GridBlock className="border border-[#1a1a1a] p-12 space-y-6">
                        <div className="flex items-center space-x-3">
                          <img src={Logo} alt="WALL-E Logo" className="w-8 h-8" />
                          <span className="text-xl font-bold">WALL-E</span>
                        </div>
                        <p className="text-gray-400">
                          Free blockchain analytics platform for everyone.
                        </p>
                      </GridBlock>
                      <GridBlock className="border border-[#1a1a1a] p-12">
                        <h4 className="text-lg font-bold mb-4">Product</h4>
                        <ul className="space-y-3">
                          <li>
                            <a href="#features" className="text-gray-400 hover:text-white">
                              Features
                            </a>
                          </li>
                          <li>
                            <a href="#how-it-works" className="text-gray-400 hover:text-white">
                              How It Works
                            </a>
                          </li>
                          <li>
                            <a href="#technology" className="text-gray-400 hover:text-white">
                              Technology
                            </a>
                          </li>
                          <li>
                            <a href="#use-cases" className="text-gray-400 hover:text-white">
                              Use Cases
                            </a>
                          </li>
                        </ul>
                      </GridBlock>
                      <GridBlock className="border border-[#1a1a1a] p-12">
                        <h4 className="text-lg font-bold mb-4">Developers</h4>
                        <ul className="space-y-3">
                          <li>
                            <a href="https://docs.agentwall-e.com" className="text-gray-400 hover:text-white">
                              Documentation
                            </a>
                          </li>
                          <li>
                          <button
                            onClick={() => {
                              alert("API documentation coming soon!");
                            }}
                            className="text-gray-400 hover:text-white text-left"
                          >
                            API (Coming Soon)
                          </button>
                          </li>
                        </ul>
                      </GridBlock>
                      <GridBlock className="border border-[#1a1a1a] p-12">
                        <h4 className="text-lg font-bold mb-4">Legal</h4>
                        <ul className="space-y-3">
                          <li>
                          <a 
                            href="https://docs.agentwall-e.com" 
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-white">
                            Documentation
                          </a>
                          </li>
                        </ul>
                      </GridBlock>
                      <GridBlock colSpan={4} className="border border-[#1a1a1a] p-8">
                        <div className="text-center text-gray-400">
                          © 2025 WALL-E. All rights reserved.
                        </div>
                      </GridBlock>
                    </GridSection>
                  </div>
                </footer>
              </div>
            </div>
          </div>

          <div className="hidden lg:block"></div>
        </div>
      </div>
    </div>
  );
};

export default App;
